import React, { useState, useLayoutEffect } from 'react';

import Portal from '../Portal/Portal';

import { hasDocument } from '../../../helpers/index';

import { elevationTokens } from '../../../tokens/globals/tokens';

/**
 * Render component through a portal if doppelganger exists
 * @param doppelganger Doppelganger element created in document.body through useDoppelganger hook
 * @param component Component to be rendered
 * @returns Either component in a <Portal /> or the component itself
 */
export const renderFloatingBox = (doppelganger, component) =>
  doppelganger ? <Portal container={doppelganger}>{component}</Portal> : component;

/**
 * Calculates doppelganger styles based upon the the position of the element relative to the <body>
 * @param element Original element that contains the floating box
 * @returns CSS styles in a template string
 */
function parseDoppelgangerStyles(element, container) {
  const elementBox = element.getBoundingClientRect();
  const documentBox = container.getBoundingClientRect();

  /* Tried various calculations to account for edge case scenarios but none
   of them were consistent. There are issues around pseudo elements and
   negative margins used in various layout elements. Since the Typeahead has
   worked thus far, this code specifically targets the AnnounceKit. If issue
   persists and appears elsewhere, then we'll revisit and allot more time. */

  let top = elementBox.top - documentBox.top + elementBox.height;
  const announceKit = container.classList.value.includes('announcekit-booster-bar');

  if (container.tagName === 'BODY' && announceKit) {
    const documentBefore = window.getComputedStyle(container, ':before').marginTop;
    top += parseInt(documentBefore, 10);
  }

  const styles = `
    position: absolute;
    left: ${elementBox.left - documentBox.left - 1}px;
    top: ${top}px;
    width: ${elementBox.width}px;
    height: 1px;
    z-index: ${elevationTokens.GlobalDoppelganger};
  `;

  return styles;
}

/**
 * Creates a duplicate of an element that contains a floating box and inserts it in the body element
 * to solve issue where absolutely positioned elements are cut by "overflow: hidden" contexts.
 * @param ref Ref object of the original element that contains the floating box
 * @param state Tracks the state of the floating box (open or closed)
 * @returns Doppelganger element
 */
const useDoppelganger = (ref, scrollableRef, state, portalContainer: HTMLDivElement | null = null) => {
  const [doppelganger, setDoppelganger] = useState<HTMLDivElement>();
  const [doppelgangerStyles, setDoppelgangerStyles] = useState<string>(``);
  const doppelgangerRef = React.useRef(hasDocument() ? document.createElement('div') : React.createElement('div'));
  const currentStateRef = React.useRef(state);

  useLayoutEffect(() => {
    if (!hasDocument()) {
      return () => {};
    }
    const currentElement = ref.current;
    const currentDoppel = doppelgangerRef.current as HTMLDivElement;

    const container = portalContainer || document.body;

    if (scrollableRef?.current) {
      setDoppelgangerStyles(parseDoppelgangerStyles(currentElement, container));
      setDoppelganger(currentDoppel);
      currentDoppel.style.cssText = doppelgangerStyles;
    }

    if (!currentStateRef.current && state && scrollableRef) {
      container.appendChild(currentDoppel);
    }

    return () => {
      if (document.body.contains(currentDoppel) && state) {
        setDoppelgangerStyles(parseDoppelgangerStyles(currentElement, container));
        container.removeChild(currentDoppel);
      }
    };
  }, [doppelganger, portalContainer, state, ref, scrollableRef, doppelgangerStyles]);

  return doppelganger;
};

export default useDoppelganger;
